import { Injectable } from '@angular/core'
import { ViewModel } from '../view-model'
import { UIEventHub } from './event.service'
import * as moment from 'moment'
import { LoadingService } from '@ar2/common'
import * as ENV from '../environment';
import { AuthorizationService, TicketManager, UserSettingsService } from '@ar2/common';

/**
 * REST APIクライアントサービス
 * @author nabla2.metamodel.generator
 */
@Injectable()
export class RestClient {

  /**
   * コンストラクタ
   * @param eventHub イベントハブ
   * @param loading: ローディングサービス
   */
  constructor(
    public eventHub:UIEventHub,
    public loading: LoadingService,
    private ticketMgr: TicketManager,
    private settings: UserSettingsService,
    private authorizationService: AuthorizationService
  ) {}

  /**
   * 前回の送信を保持する
   */
  public call__pre:ApiCall

  /**
   * システムエラーメッセージ
   */
  private GLOBAL_ERROR = 'Something went wrong. Please try again later.'

  /**
   * サーバーエラー
   */
  private INTERNAL_SERVER_ERROR = 500

  /**
   * APIリクエストを送信する。
   * @param props 送信定義
   * @param model 画面表示モデル
   */
  send(call:ApiCall, model:ViewModel, file:File, ignoreWarning:any = null):ApiCall {
    this.call__pre = call
    const usesQs = (call.props.method === 'GET')
    const usesBody = (call.props.method !== 'GET')
    const headers:Headers = new Headers()
    const ticket = this.ticketMgr.get();
    const language = this.settings.getSettings().language;
    headers.set('Request-Language', language);
    if (ticket) {
      headers.set('Authorization', `Bearer ${ticket}`);
      headers.set('X-Requested-Gbu-Cd', this.authorizationService.getSelectedGbuCD() || '');
    
    }
    headers.append('Accept', 'application/json, text/plain, */*')
    if (!file) headers.append('Content-Type', 'application/json')
    headers.append('X-User-Time-Zone', moment().format('Z'))
    const payload = call.props.payload(model)
    if (ignoreWarning) payload.ignoreWarning = ignoreWarning
    const url = call.props.url(model) + (usesQs ? this.queryString(payload) : '')
    const formData = new FormData()
    if (file) {
      formData.append('file', file)
    }
    if (!this.loading.showLoading) {
      this.loading.showLoading = true
    }
    fetch(url, {
      method: call.props.method,
      headers: headers,
      credentials: 'same-origin',
      ... usesBody && {body: file ? formData : JSON.stringify(payload)}
    }).then(response => {
      this.eventHub.next({
        type: 'api_response',
        id: call.props.id,
        source: {
          apiCall: call,
          response: response,
        }
      })
      if (this.loading.showLoading) {
        this.loading.showLoading = false
      }
      if (response.status >= this.INTERNAL_SERVER_ERROR) {
        throw response
      }
    }).catch(e => {
      if (this.loading.showLoading) {
        this.loading.showLoading = false
      }
      console.log(`An error occured while calling the api: ${call.props.id}`)
      throw this.GLOBAL_ERROR
    })
    return call
  }

  queryString(params:any):string {
    const keys = Object.keys(params)
    if (!keys.length) return ''
    return '?' + keys.map(key => {
      const value = params[key]
      const isArray = Array.isArray(value)
      if (isArray && !value.length) return null
      return isArray
           ? value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&')
           : `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    }).filter(e => e != null).join('&')
  }
}

export class ApiCallProps {
  /** API呼び出し名 */
  id: string
  /** HTTPメソッド */
  method: string
  /** APIエンドポイントURL */
  url: (model:ViewModel) => string
  /** ファイルとしてダウンロード */
  downloadAsFile?: boolean = false
  /** リクエストボディ内容を返す関数 */
  payload: (model:ViewModel) => any
  /** レスポンスボディを画面の表示モデルに反映する関数 */
  modelUpdate: (model:ViewModel, body:any) => ViewModel
  /** レスポンスボディ中のエラーメッセージを画面の表示モデルに反映する関数 */
  errorUpdate: (model:ViewModel, body:any) => ViewModel
  /** レスポンスボディ中のワーニングメッセージを画面の表示モデルに反映する関数 */
  warningUpdate: (model:ViewModel, body:any) => ViewModel
}

export class ApiCall {

  props:ApiCallProps

  constructor(props:ApiCallProps) {
    this.props = props
  }

  get id():string {
    return this.props.id
  }

  get downloadAsFile():boolean {
    return this.props.downloadAsFile || false
  }

  /**
   * APIのレスポンス内容を元に画面表示モデルを更新した結果を返す。
   * 変更元表示モデルの状態は不変である。
   * @param model 変更元表示モデル
   */
  updateWithResponse(model:ViewModel, body:any):ViewModel {
    return this.props.modelUpdate(model, body)
  }

  /**
   * APIからユーザエラーが返された場合に、そのレスポンス内容を反映したモデルを返す。
   * 変更元表示モデルの状態は不変である。
   * @param model 変更元表示モデル
   */
  updateWithUserError(model:ViewModel, body:any):ViewModel {
    return this.props.errorUpdate(model, body)
  }

  /**
   * APIからユーザワーニングが返された場合に、そのレスポンス内容を反映したモデルを返す。
   * 変更元表示モデルの状態は不変である。
   * @param model 変更元表示モデル
   */
  updateWithUserWarning(model:ViewModel, body:any):ViewModel {
    return this.props.warningUpdate(model, body)
  }
}


// WEBPACK FOOTER //
// ./src/services/rest-client.service.ts